import { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { DollarSign, Wallet, ArrowUpRight, ShieldCheck, ExternalLink, RotateCcw, AlertCircle, Landmark } from 'lucide-react';

const CARD = { backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.05)', border: '1px solid #EFEAE0' };

function Stat({ Icon, tint, label, value, hint }) {
  return (
    <div className="p-5 rounded-2xl relative overflow-hidden" style={CARD}>
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-10" style={{ backgroundColor: tint }} />
      <div className="relative flex items-center gap-4">
        <div className="p-3 rounded-2xl" style={{ backgroundColor: `${tint}20` }}>
          <Icon className="w-6 h-6" style={{ color: tint }} />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D', letterSpacing: '0.1em' }}>{label}</p>
          <p className="text-2xl font-bold leading-tight" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{value}</p>
          {hint && <p className="text-[11px]" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{hint}</p>}
        </div>
      </div>
    </div>
  );
}

export default function EarningsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/admin/earnings');
        if (mounted) setData(data);
      } catch {}
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) return <div className="p-12 rounded-3xl text-center" style={CARD}><p style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Loading earnings…</p></div>;
  if (!data) return <div className="p-12 rounded-3xl text-center" style={CARD}><p style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Could not load earnings.</p></div>;

  const { totals, by_tier, recent } = data;
  const fmt = (n) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="earnings-tab">
      {/* Compliance banner — LLC / personal-vs-business */}
      <div className="p-5 rounded-3xl relative overflow-hidden" style={{ ...CARD, background: 'linear-gradient(135deg, #FDFBF7 0%, #FFFFFF 100%)' }}>
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl flex-shrink-0" style={{ backgroundColor: '#78A18E20' }}>
            <ShieldCheck className="w-5 h-5" style={{ color: '#78A18E' }} />
          </div>
          <div className="flex-1">
            <h3 className="font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Money stays in Stripe until you release it</h3>
            <p className="text-sm mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
              Every consultation payment is recorded here <em>and</em> held safely in your Stripe balance. Nothing hits your bank until you switch on payouts. Perfect while your LLC paperwork is finalizing — keep business and personal funds separate.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href="https://dashboard.stripe.com/settings/payouts"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between px-4 py-3 rounded-xl transition hover:-translate-y-0.5"
                style={{ backgroundColor: '#635BFF', color: '#FFFFFF', fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: 13 }}
                data-testid="stripe-payouts-link"
              >
                <span className="flex items-center gap-2"><Landmark className="w-4 h-4" /> Set payouts to Manual</span>
                <ExternalLink className="w-4 h-4 opacity-80" />
              </a>
              <a
                href="https://dashboard.stripe.com/balance"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between px-4 py-3 rounded-xl transition hover:-translate-y-0.5"
                style={{ backgroundColor: '#FFFFFF', color: '#635BFF', border: '1px solid #635BFF', fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: 13 }}
                data-testid="stripe-balance-link"
              >
                <span className="flex items-center gap-2"><Wallet className="w-4 h-4" /> Open Stripe Balance</span>
                <ExternalLink className="w-4 h-4 opacity-80" />
              </a>
            </div>
            <p className="text-xs mt-3 flex items-start gap-1.5" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#E8C265' }} />
              <span>Once your LLC is formed, add your <strong>business bank account</strong> in Stripe and remove any personal account so nothing accidentally goes to the wrong place.</span>
            </p>
          </div>
        </div>
      </div>

      {/* Big stat grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat Icon={Wallet} tint="#78A18E" label="Net Earnings" value={fmt(totals.net_earnings)} hint="Gross − Refunded" />
        <Stat Icon={DollarSign} tint="#5B7C99" label="Gross Paid" value={fmt(totals.gross_paid)} hint="All successful payments" />
        <Stat Icon={ArrowUpRight} tint="#E8C265" label="Held in Stripe" value={fmt(totals.pending_payout)} hint="Ready when you say so" />
        <Stat Icon={RotateCcw} tint="#B77BB0" label="Refunded" value={fmt(totals.refunded)} hint="Returned to clients" />
      </div>

      {/* Awaiting delivery mini-banner */}
      {totals.awaiting_delivery > 0 && (
        <div className="p-4 rounded-2xl flex items-center gap-3" style={{ backgroundColor: '#FFF5E5', border: '1px solid #E8C26540' }}>
          <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#E8C265' }} />
          <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
            <strong style={{ color: '#2C2A29' }}>{fmt(totals.awaiting_delivery)}</strong> from paid consultations you haven't responded to yet. Deliver those before considering the money "earned".
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By tier */}
        <div className="p-6 rounded-3xl" style={CARD}>
          <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Earnings by Tier</h3>
          <div className="space-y-3">
            {by_tier.map((t, idx) => {
              const tint = ['#78A18E', '#5B7C99', '#E57A44'][idx % 3];
              return (
                <div key={t.tier} className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: '#FDFBF7' }}>
                  <div className="flex items-center gap-3">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tint }} />
                    <span style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontWeight: 600 }}>{t.label}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{fmt(t.amount)}</p>
                    <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{t.count} paid</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick summary panel (replaces monthly gross/refund chart) */}
        <div className="p-6 rounded-3xl" style={CARD}>
          <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>At a Glance</h3>
          <div className="space-y-3 text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <p style={{ color: '#595553' }}>
              <strong style={{ color: '#2C2A29' }}>Every payment is safely recorded here</strong> and held in your Stripe balance. Nothing moves to your bank until you enable payouts.
            </p>
            <div className="pt-2 mt-2 space-y-2" style={{ borderTop: '1px dashed #E6E2D8' }}>
              <div className="flex justify-between"><span style={{ color: '#8C867D' }}>Total consultations paid</span><span style={{ color: '#2C2A29', fontWeight: 600 }}>{recent.length}</span></div>
              <div className="flex justify-between"><span style={{ color: '#8C867D' }}>Awaiting your response</span><span style={{ color: '#E57A44', fontWeight: 600 }}>{fmt(totals.awaiting_delivery)}</span></div>
              <div className="flex justify-between"><span style={{ color: '#8C867D' }}>Ready to release from Stripe</span><span style={{ color: '#78A18E', fontWeight: 600 }}>{fmt(totals.pending_payout)}</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent transactions */}
      <div className="p-6 rounded-3xl" style={CARD}>
        <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Recent Payments</h3>
        {recent.length === 0 ? (
          <p className="py-6 text-center" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>No paid consultations yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #EFEAE0' }}>
                  <th className="text-left py-2 px-3" style={{ color: '#8C867D', fontWeight: 600 }}>Client</th>
                  <th className="text-left py-2 px-3" style={{ color: '#8C867D', fontWeight: 600 }}>Tier</th>
                  <th className="text-left py-2 px-3" style={{ color: '#8C867D', fontWeight: 600 }}>Status</th>
                  <th className="text-left py-2 px-3" style={{ color: '#8C867D', fontWeight: 600 }}>Paid</th>
                  <th className="text-right py-2 px-3" style={{ color: '#8C867D', fontWeight: 600 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #F5F1E8' }}>
                    <td className="py-2.5 px-3" style={{ color: '#2C2A29' }}>{r.user_name}</td>
                    <td className="py-2.5 px-3 capitalize" style={{ color: '#595553' }}>{r.tier}</td>
                    <td className="py-2.5 px-3">
                      <span
                        className="px-2 py-0.5 rounded-full text-[11px] font-semibold"
                        style={{
                          backgroundColor: r.payment_status === 'refunded' ? '#B77BB020' : r.status === 'completed' ? '#78A18E20' : '#E8C26520',
                          color: r.payment_status === 'refunded' ? '#B77BB0' : r.status === 'completed' ? '#78A18E' : '#E8C265',
                        }}
                      >
                        {r.payment_status === 'refunded' ? 'refunded' : r.status || 'paid'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3" style={{ color: '#8C867D' }}>{r.paid_at ? new Date(r.paid_at).toLocaleDateString() : '—'}</td>
                    <td className="py-2.5 px-3 text-right font-semibold" style={{ color: r.payment_status === 'refunded' ? '#8C867D' : '#2C2A29', textDecoration: r.payment_status === 'refunded' ? 'line-through' : 'none' }}>{fmt(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
